import { useEffect } from "react";
import { Icon } from "@/ds";
import { useDismissible } from "@/lib/useDismissible";

const AXEN_URL = "https://axen.software";
const STORAGE_KEY = "axen-popout-dismissed";
const DELAY_MS = 2500;

/**
 * Studio credit as a corner popout.
 *
 * Deliberately NOT a modal: it never covers the page content, so it stays clear
 * of Google's intrusive-interstitial guidance for mobile and does not stand
 * between a reader and the portfolio. That also means no focus trap and no
 * `aria-modal` — the page behind stays fully interactive, which is the point.
 *
 * Ink surface rather than a card, so the page holds to the system's limit of
 * two background tones (page + ink).
 */
export function AxenPopout() {
  const { visible, dismiss } = useDismissible(STORAGE_KEY, DELAY_MS);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <aside
      aria-label="Site credit"
      className="fixed bottom-5 left-5 right-5 md:right-auto z-30 md:max-w-[360px] animate-popout"
    >
      <div className="relative bg-surface-ink text-on-ink border border-border-on-ink shadow-raise p-5 flex flex-col gap-3">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-[28px] h-[28px] text-on-ink-muted hover:text-on-ink transition-colors duration-160 ease-out cursor-pointer"
        >
          <Icon name="x" size={16} />
        </button>

        <span className="font-label text-label uppercase text-on-ink-muted">Built under</span>
        <span className="font-display text-[24px] leading-[1.1] text-on-ink pr-8">Axen Software</span>
        <p className="text-body-sm text-on-ink-body">
          This portfolio was designed and built by Axen Software, a software studio based in Indonesia.
        </p>

        <a
          href={AXEN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 hover:gap-4 self-start mt-1 font-label text-label-lg uppercase text-on-ink border-b border-border-on-ink pb-1 transition-[gap,color,border-color] duration-160 ease-out hover:text-signal-500 hover:border-signal-500"
        >
          axen.software
          <Icon name="arrow-right" size={14} />
        </a>
      </div>
    </aside>
  );
}
